import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
// hooks
import { useAddReferencedItem } from 'containers/MinimizedObjectivesList/layers/Business/useAddReferencedItem'
// utils
import { handleError } from 'utils/errors/handlers'
import { get, isFunction } from 'lodash'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { MinimizedObjectivesListAPIContext } from 'containers/MinimizedObjectivesList/layers/API'
import { MinimizedObjectivesListActionsContext } from 'containers/MinimizedObjectivesList/layers/Business/Actions'
import { SelectedPlanItemsContext } from 'containers/SubPlan/layers/BL/SelectedItems'
// constants

type Props = React.PropsWithChildren<unknown>

interface IContext {
  shouldShowModalActions: boolean
  handleSetShowModalActions: (value: boolean) => void
  handleAddToSubPlan: (node: any) => Promise<void>
  handleRemoveFromSubPlan: (nodeId: string, referencedNodeId: string) => Promise<void>
}

export const DetailsModalActionsContext = React.createContext<IContext>({} as any)

const DetailsModalActions: React.FC<Props> = ({ children }) => {
  // state
  const [showActions, setShowActions] = React.useState(false)

  // context
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)
  const { selectedPlanItems, selectPlanItem, removePlanItem } = React.useContext(MinimizedObjectivesListActionsContext)
  const { deletePlanItemMutation } = React.useContext(MinimizedObjectivesListAPIContext)
  const { removePlanItem: removePlanItemFromMinimizedObjectivesList } = React.useContext(SelectedPlanItemsContext)

  // hooks
  const router = useRouter()

  const addReferencedItem = useAddReferencedItem({ selectedPlanItems, selectPlanItem })

  // methods
  const handleSetShowModalActions = (value) => setShowActions(value)

  const handleAddToSubPlan = async (node) => {
    const sourceItems = node.sourceItems || []
    const treePosition = sourceItems.find((item) => item.entityId === node.id)?.treePosition

    await addReferencedItem({
      entityType: node.entityType,
      entityId: node.id,
      treePosition,
      planId: router.query.id as string,
      sourceItems,
      sourcePlanItemId: node.planItem.id
    })
  }

  const handleRemoveFromSubPlan = async (id, referencedId) => {
    const filteredNode = selectedPlanItems.filter((entity) => entity.prevId === id)

    try {
      await deletePlanItemMutation({
        variables: {
          id: get(filteredNode, '[0].newId') || referencedId,
          deleteSource: false,
          checkChildren: true
        }
      })

      if (isFunction(treeQueryRefetch)) {
        await treeQueryRefetch()
      }

      removePlanItem(id)
      removePlanItemFromMinimizedObjectivesList(id)
    } catch (errors) {
      handleError(errors, 'ObjectivesListDetailsModalActions.handleRemoveFromSubPlan')
    }
  }

  const value = {
    shouldShowModalActions: showActions,
    handleSetShowModalActions,
    handleAddToSubPlan,
    handleRemoveFromSubPlan
  }

  return <DetailsModalActionsContext.Provider value={value}>{children}</DetailsModalActionsContext.Provider>
}

export default DetailsModalActions
